import PublicNav from '../components/PublicNav';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import PageMeta from '../components/PageMeta';
import PaywallReturnLink from '../components/PaywallReturnLink';

const EFFECTIVE_DATE = 'September 30, 2026';

export default function Terms() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', flexDirection: 'column' }}>
      <PageMeta title="Terms of Service" description="Read the Agence Terms of Service — covering your rights, our responsibilities, and the rules for using the AI-powered finance and investment copilot." path="/terms" />
      <PublicNav />
      <main style={{ flex: 1, maxWidth: 720, margin: '0 auto', padding: '2rem 1.5rem 4rem', width: '100%' }}>
        <PaywallReturnLink />
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          Terms of Service
        </h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '2.5rem' }}>
          Effective {EFFECTIVE_DATE}
        </p>

        <Section title="Acceptance of Terms">
          By creating an account or using Agence ("the Service"), you agree to these Terms of
          Service ("Terms"). If you do not agree, do not use the Service. These Terms form a
          binding agreement between you and <strong>Kalotech LLC</strong>, a Florida limited
          liability company operating Agence ("we," "us," or "our").
        </Section>

        <Section title="Description of Service">
          Agence is a personal finance and investment analysis tool that:
          <ul style={listStyle}>
            <li>Connects to bank accounts via Plaid to retrieve transaction and balance data.</li>
            <li>Connects to supported brokerage accounts via OAuth to retrieve portfolio data and, at your direction, execute trades in your brokerage account.</li>
            <li>Runs AI agents in parallel to analyze your financial data and surface insights and trade signals.</li>
            <li>Generates automated paper (simulated) trade signals based on automation rules you configure, which on standard plans require your approval before each order is placed.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            The Service is provided for informational and educational purposes only. Agence is
            not a broker-dealer, investment adviser, or financial planner.
          </p>
        </Section>

        <Section title="Trading Modes">
          Agence supports two distinct trading modes:
          <ul style={listStyle}>
            <li>
              <strong>Paper trading (automated simulation).</strong> The autopilot feature
              evaluates your configured automation rules on a schedule and generates simulated
              trade signals in a paper account. On standard plans, each signal requires your
              review and approval before the simulated order is placed — the same as live
              trading, described below. No real money is transferred, no real securities are
              bought or sold, and no real brokerage account is debited or credited regardless of
              execution mode. Paper results do not reflect what would occur in live markets, and
              past simulated performance is not indicative of future real-world results.
            </li>
            <li>
              <strong>Self-directed live trading.</strong> Agence may generate trade signals
              and suggestions based on your connected portfolio and configured rules.{' '}
              <strong>You are solely responsible for reviewing, approving, and placing each
              real-money order</strong> through your connected brokerage. Agence does not
              execute real-money trades on your behalf and does not hold, custody, or transfer
              your funds. Your brokerage account, KYC, and order execution are governed solely
              by your brokerage's terms and conditions.
            </li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            For a full description of automation behavior, risk controls, and signal methodology,
            see the{' '}
            <Link to="/automation-disclosure" style={linkStyle}>Automation Risk Disclosure</Link>,
            which is incorporated into these Terms by reference.
          </p>
        </Section>

        <Section title="Not Financial Advice">
          <strong>Nothing in Agence constitutes financial, investment, tax, or legal advice.</strong>{' '}
          Insights, agent outputs, chat responses, automation signals, and trade suggestions are
          generated by automated AI systems and are provided for informational purposes only.
          They do not account for your complete financial situation, risk tolerance, tax
          circumstances, or investment objectives. AI-generated outputs may contain errors,
          omissions, or suggestions unsuitable for your specific circumstances; AI models can
          fail silently, reflect systematic biases, or produce outdated analysis without warning.
          <p style={{ marginTop: '0.75rem' }}>
            You should consult a qualified financial advisor before making any real investment,
            tax, or financial decisions. Agence is not a registered investment adviser,
            broker-dealer, or financial planner. Agence does not supervise, endorse, or
            guarantee the accuracy of any signal, insight, or trade suggestion.
          </p>
        </Section>

        <Section title="Subscriptions and Billing">
          Agence offers a free tier and a paid Plus plan ("Plus"). By subscribing to Plus:
          <ul style={listStyle}>
            <li>You authorize recurring charges to your payment method at the rate displayed at checkout (monthly or annual billing, as selected).</li>
            <li>A 7-day free trial is provided for first-time Plus subscribers. No charge is made until the trial period ends. You may cancel before day 8 to avoid any charge.</li>
            <li>Subscriptions renew automatically at the end of each billing period. You may cancel at any time — a subscription bought on the web from Account → Subscription, and one bought in the iOS or Android app from your Apple ID or Google Play subscription settings; cancellation takes effect at the end of the current paid period and no partial-period refunds are issued.</li>
            <li>Web charges are in USD; in-app purchases are charged in the currency your app store shows at checkout, and refunds for those are handled by Apple or Google under their own policies. All charges are otherwise non-refundable except as required by applicable law or at our sole discretion.</li>
            <li>If a renewal payment fails, we retry it for a limited period and Plus remains available while we do. If the payment still can't be collected, your subscription ends and your account returns to the free tier.</li>
            <li>If you dispute a subscription charge with your card issuer (a chargeback), we may cancel your subscription immediately and end paid access. You're welcome to resubscribe at any time.</li>
            <li>We reserve the right to change subscription pricing with at least 30 days' advance notice to your registered email address. Continued use of Plus after the notice period constitutes acceptance of the new pricing.</li>
          </ul>
        </Section>

        <Section title="Eligibility">
          You must be at least 18 years old to use the Service. By using Agence, you represent
          that you meet this requirement. The Service is intended for personal, non-commercial use.
        </Section>

        <Section title="Account Registration">
          <ul style={listStyle}>
            <li>You must provide a valid email address and a password of at least 8 characters.</li>
            <li>You are responsible for maintaining the confidentiality of your credentials and for all activity under your account.</li>
            <li>You must notify us immediately of any unauthorized access to your account.</li>
            <li>One person may not maintain more than one account.</li>
          </ul>
        </Section>

        <Section title="Third-Party Integrations">
          Agence integrates with Plaid, supported broker integrations, and third-party data
          providers. By connecting these services, you also agree to their respective terms:
          <ul style={listStyle}>
            <li>
              <a href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                Plaid End User Privacy Policy
              </a>
            </li>
            <li>
              Your connected brokerage's terms and disclosures. Your brokerage is a separate
              legal entity from Agence; account opening, KYC, custody, and order execution are
              governed solely by your brokerage's terms. Agence does not open, maintain, or
              custody brokerage accounts on your behalf.
            </li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            Agence is not responsible for the availability, accuracy, or actions of these
            third-party services. If a third-party service is unavailable, features of Agence
            that depend on it may not function.
          </p>
        </Section>

        <Section title="Service Suspension and Automated-Feature Kill-Switch">
          We reserve the right to suspend, pause, or disable automated features (including
          autopilot and live-trading signal generation) at any time and without prior notice if:
          <ul style={listStyle}>
            <li>We detect unusual account activity, suspected fraud, or a security incident.</li>
            <li>A connected third-party service (bank connection, brokerage OAuth, or market data provider) becomes unavailable or returns anomalous data.</li>
            <li>Continued operation poses a risk to you, other users, or the Service.</li>
            <li>Required by applicable law, regulation, or a directive from a regulatory authority.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            We will make reasonable efforts to notify you promptly when automated features are
            suspended, but pre-suspension notification is not guaranteed.
          </p>
        </Section>

        <Section title="Acceptable Use">
          You agree not to:
          <ul style={listStyle}>
            <li>Use the Service for any unlawful purpose or in violation of any applicable law.</li>
            <li>Attempt to reverse-engineer, decompile, or extract source code from the Service.</li>
            <li>Interfere with or disrupt the integrity or performance of the Service or its infrastructure.</li>
            <li>Use automated scripts, bots, or scrapers against the Service beyond normal use.</li>
            <li>Share your account credentials with third parties or use another person's credentials.</li>
            <li>Use the Service to process financial data belonging to others without their explicit consent.</li>
          </ul>
        </Section>

        <Section title="Data and Privacy">
          Your use of the Service is subject to our{' '}
          <Link to="/privacy" style={linkStyle}>Privacy Policy</Link>, which is incorporated
          into these Terms by reference.
        </Section>

        <Section title="Intellectual Property">
          The Service, including its design, codebase, agent architecture, and content, is owned
          by Agence and its operators. You are granted a limited, non-exclusive, non-transferable
          license to use the Service for personal purposes. You may not reproduce, distribute,
          or create derivative works from any part of the Service without written permission.
          <p style={{ marginTop: '0.75rem' }}>
            You retain ownership of your personal financial data. By using the Service, you grant
            Agence a limited license to process your data solely to provide and improve the Service.
          </p>
        </Section>

        <Section title="Disclaimer of Warranties">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
          EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. AGENCE DOES NOT WARRANT THAT
          THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT AI-GENERATED INSIGHTS, SIGNALS,
          OR SUGGESTIONS WILL BE ACCURATE, COMPLETE, OR SUITABLE FOR YOUR CIRCUMSTANCES.
          AI MODELS MAY PRODUCE INCORRECT, BIASED, OR OUTDATED OUTPUT WITHOUT WARNING.
        </Section>

        <Section title="Limitation of Liability">
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, AGENCE AND ITS OPERATORS SHALL NOT
          BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
          OR ANY LOSS OF PROFITS, DATA, OR INVESTMENT RETURNS, ARISING OUT OF OR RELATED TO
          YOUR USE OF THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. THIS
          INCLUDES, WITHOUT LIMITATION, ANY LOSSES ARISING FROM TRADE SIGNALS, SUGGESTIONS,
          OR AUTOMATED ACTIONS GENERATED BY THE SERVICE, AND ANY REAL-MONEY TRADING DECISIONS
          YOU MAKE IN CONNECTION WITH THOSE SIGNALS.
          <p style={{ marginTop: '0.75rem' }}>
            AGENCE'S TOTAL LIABILITY TO YOU FOR ANY CLAIM SHALL NOT EXCEED THE GREATER OF
            (A) THE AMOUNT YOU PAID FOR THE SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM,
            OR (B) ONE HUNDRED U.S. DOLLARS ($100).
          </p>
        </Section>

        <Section title="Force Majeure">
          Agence shall not be liable for any delay or failure to perform arising from causes
          beyond our reasonable control, including but not limited to: acts of God, natural
          disasters, war, terrorism, labor disputes, internet or network outages, failures of
          third-party APIs or data providers (including bank connections, brokerage OAuth
          services, or market data feeds), regulatory actions, or extreme market conditions
          that render automated features unsafe or impractical to operate.
        </Section>

        <Section title="Dispute Resolution — Arbitration and Class Action Waiver">
          <p style={{ marginBottom: '0.75rem' }}>
            <strong>Binding Arbitration.</strong> Except for claims that qualify for small
            claims court, any dispute, claim, or controversy arising out of or relating to
            these Terms or the Service shall be resolved by binding individual arbitration
            administered by the American Arbitration Association ("AAA") under its Consumer
            Arbitration Rules, rather than in a court of law. There is no judge or jury in
            arbitration. The arbitrator's award is binding and may be entered as a judgment
            in any court of competent jurisdiction.
          </p>
          <p style={{ marginBottom: '0.75rem' }}>
            <strong>Class Action Waiver.</strong> YOU AND AGENCE EACH AGREE THAT ANY
            PROCEEDINGS TO RESOLVE DISPUTES WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS
            AND NOT AS A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION. YOU MAY NOT
            PARTICIPATE AS A CLASS MEMBER IN ANY CLASS ACTION ARISING OUT OF OR RELATED
            TO THESE TERMS OR THE SERVICE.
          </p>
          <p>
            <strong>Opt-Out.</strong> You may opt out of the arbitration and class-action
            waiver provisions by sending written notice to{' '}
            <a href="mailto:support@agence.finance" style={linkStyle}>support@agence.finance</a>{' '}
            within 30 days of first accepting these Terms. Your notice must include your
            name, email address, and a clear statement that you are opting out of arbitration.
            Opting out does not affect any other provision of these Terms.
          </p>
        </Section>

        <Section title="Termination">
          You may stop using the Service and request deletion of your account at any time by
          contacting us. We reserve the right to suspend or terminate your account if you
          violate these Terms or if continued operation of your account poses a risk to the
          Service or other users. Upon termination, your right to use the Service ceases
          immediately. Active subscriptions will not be refunded upon termination for cause.
        </Section>

        <Section title="Changes to These Terms">
          We may update these Terms as the Service evolves. Material changes will be communicated
          via in-app notification or email with reasonable advance notice. Continued use of the
          Service after changes take effect constitutes acceptance of the updated Terms.
        </Section>

        <Section title="Governing Law">
          These Terms are governed by the laws of the State of Florida, without regard to its
          conflict of law provisions. To the extent any dispute is not subject to arbitration
          under these Terms, it shall be resolved in the courts located in Miami-Dade County,
          Florida, and you consent to personal jurisdiction in those courts.
        </Section>

        <Section title="Contact">
          Questions about these Terms:{' '}
          <a href="mailto:support@agence.finance" style={linkStyle}>support@agence.finance</a>
        </Section>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2 style={{
        fontSize: '1.05rem',
        fontWeight: 600,
        color: 'var(--text-primary)',
        marginBottom: '0.6rem',
        paddingBottom: '0.4rem',
        borderBottom: '1px solid var(--navy-200)',
      }}>
        {title}
      </h2>
      <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
        {children}
      </div>
    </section>
  );
}

const listStyle = {
  margin: '0.25rem 0 0 1.25rem',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
};

const linkStyle = {
  color: 'var(--navy-600)',
  textDecoration: 'underline',
};
